import { Stack, Typography } from '@mui/joy';
import dayjs from 'dayjs';

import { DEFAULT_DISPLAY_DATE_FORMAT } from 'core/constants';
import { IInfoCardLine } from 'core/types';
import { ITradeAgreementOwner } from 'corporate/types';
import { AttributeProjectKind } from 'quotes/enums';
import {
  IAttributeCustomer,
  IAttributeLocation,
  IAttributeProject,
  IAttributes,
} from 'quotes/types';
import { Color, Direction, Level } from 'theme/enums';

import { ExpandableValueList, IExpandableValueListItem } from './ExpandableValueList';

export const attributesColumnGridSizing = { xs: 12, md: 6, lg: 4 };

// Empty rows return `undefined` (not an empty element) so InfoCard's Line renders the
// default dash instead of a blank cell.

export const renderProjectValue = (
  project: IAttributeProject | null,
  projectNumberLabel: string,
  programNumberLabel: string,
  placeholder: string
): IInfoCardLine['value'] => {
  if (!project) {
    return undefined;
  }

  return (
    <Stack direction={Direction.COLUMN} sx={{ alignItems: 'flex-end' }}>
      <Typography level={Level.BODY_SM} sx={{ fontWeight: 600 }}>
        {project.name || placeholder}
      </Typography>

      {project.projectNumber && (
        <Typography level={Level.BODY_XS} color={Color.NEUTRAL}>
          {projectNumberLabel}: {project.projectNumber}
        </Typography>
      )}

      {project.programNumber && (
        <Typography level={Level.BODY_XS} color={Color.NEUTRAL}>
          {programNumberLabel}: {project.programNumber}
        </Typography>
      )}
    </Stack>
  );
};

export const renderTradeAgreementOwnerValue = (
  owner: ITradeAgreementOwner | null,
  placeholder: string
): IInfoCardLine['value'] => {
  if (!owner) {
    return undefined;
  }

  return (
    <Stack direction={Direction.COLUMN} sx={{ alignItems: 'flex-end' }}>
      <Typography level={Level.BODY_SM} sx={{ fontWeight: 600 }}>
        {owner.name || placeholder}
      </Typography>

      {owner.email && (
        <Typography level={Level.BODY_XS} color={Color.NEUTRAL}>
          {owner.email}
        </Typography>
      )}
    </Stack>
  );
};

/**
 * An unset date returns undefined rather than the placeholder, so InfoCard renders its dash. The
 * field is required now, so blank only means a quote configured before that rule — the dash is
 * what sends the user to the Attributes form. An unparseable stored value degrades to the same
 * dash.
 */
export const renderTradeAgreementStartDateValue = (
  tradeAgreementStartDate: string | null | undefined
): IInfoCardLine['value'] => {
  if (!tradeAgreementStartDate) {
    return undefined;
  }

  const parsed = dayjs(tradeAgreementStartDate);

  return parsed.isValid() ? parsed.format(DEFAULT_DISPLAY_DATE_FORMAT) : undefined;
};

export const renderCustomersValue = (
  attributes: IAttributes,
  allCustomersLabel: string,
  accountNumberLabel: string,
  moreLabel: string,
  placeholder: string
): IInfoCardLine['value'] => {
  // A program (or a project of unknown kind) is sent as All Customers with no customers stored.
  const isProgram =
    !!attributes.project?.id && AttributeProjectKind.PROJECT !== attributes.project.kind;

  if (attributes.allCustomers || isProgram) {
    const allCustomersItem: IExpandableValueListItem = { id: 0, name: allCustomersLabel };

    return <ExpandableValueList items={[allCustomersItem]} moreLabel={moreLabel} />;
  }

  if (attributes.customers.length === 0) {
    return undefined;
  }

  const items: IExpandableValueListItem[] = attributes.customers.map(
    (customer: IAttributeCustomer) => ({
      id: customer.id,
      name: customer.name || placeholder,
      sublineLabel: accountNumberLabel,
      sublineValue: customer.accountsReceivableId,
    })
  );

  return <ExpandableValueList items={items} moreLabel={moreLabel} />;
};

export const renderLocationsValue = (
  locations: IAttributeLocation[],
  locationIdLabel: string,
  moreLabel: string,
  placeholder: string,
  // Sales locations only — shipping never has an "all" mode.
  isAll = false,
  allLocationsLabel = ''
): IInfoCardLine['value'] => {
  if (isAll) {
    const allLocationsItem: IExpandableValueListItem = { id: 0, name: allLocationsLabel };

    return <ExpandableValueList items={[allLocationsItem]} moreLabel={moreLabel} />;
  }

  if (locations.length === 0) {
    return undefined;
  }

  const items: IExpandableValueListItem[] = locations.map((location: IAttributeLocation) => ({
    id: location.id,
    name: location.name || placeholder,
    sublineLabel: locationIdLabel,
    sublineValue: location.externalLocationId,
  }));

  return <ExpandableValueList items={items} moreLabel={moreLabel} />;
};
